import React, { createContext, useState, useEffect } from "react";
import constant from "../constant/employee.json";
import { EmployeeService } from "../services/employeeService";

export const DashboardContext = createContext();

export const DashboardProvider = ({ children }) => {
  const [statsData, setStatsData] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const [hiringData, setHiringData] = useState([]);
  const [skillsData, setSkillsData] = useState([]);
  const [docsData, setDocsData] = useState(null);
  const [employeesData, setEmployeesData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [summaryData, setSummaryData] = useState(null);

  useEffect(() => {
    setStatsData(constant.stats || []);
    setTeamData(constant.teamDistribution || null);
    setHiringData(constant.hiringTrend || []);
    setSkillsData(constant.topSkills || []);
    setDocsData(constant.documentVerification || null);
    setSummaryData(constant.employeeSummary || null);
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await EmployeeService.getAllEmployees(currentPage);
      const apiEmployees = response.data || [];

      // Check if we have any locally added employees stored during this session and prepend them
      const localAdded = JSON.parse(
        localStorage.getItem("locally_added_employees") || "[]",
      );
      const combinedEmployees = [...localAdded, ...apiEmployees];

      setEmployeesData(combinedEmployees);
      setTotalPages(response.meta.last_page || 1);
      setTotalEmployees(
        (response.meta.total || apiEmployees.length) + localAdded.length,
      );
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [currentPage]);

  // Function to instantly inject newly added employee into state & session cache
  const handleNewEmployeeAdded = (newEmp) => {
    const formattedEmp = {
      uuid: newEmp.uuid || `local-${Date.now()}`,
      employee_code:
        newEmp.employee_code || `EMP-00${Math.floor(20 + Math.random() * 80)}`,
      full_name: newEmp.full_name || `${newEmp.first_name} ${newEmp.last_name}`,
      official_email: newEmp.official_email,
      primary_phone: newEmp.primary_phone,
      gender: newEmp.gender || "male",
      status: newEmp.status || "active",
      remarks: newEmp.remarks || "Software Developer",
      created_at: new Date().toISOString(),
    };

    // Save to session storage cache so it survives tab switches and displays instantly
    const existingLocal = JSON.parse(
      localStorage.getItem("locally_added_employees") || "[]",
    );
    const updatedLocal = [formattedEmp, ...existingLocal];
    localStorage.setItem(
      "locally_added_employees",
      JSON.stringify(updatedLocal),
    );

    // Update live state immediately
    setEmployeesData((prev) => [formattedEmp, ...prev]);
    setTotalEmployees((prev) => prev + 1);
  };

  return (
    <DashboardContext.Provider
      value={{
        statsData,
        teamData,
        hiringData,
        skillsData,
        docsData,
        employeesData,
        summaryData,
        currentPage,
        setCurrentPage,
        totalPages,
        totalEmployees,
        refreshEmployees: fetchEmployees,
        addEmployeeToState: handleNewEmployeeAdded,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};
